import {
	Button,
	Card,
	CardBody,
	Chip,
	Input,
	Typography
} from "@material-tailwind/react";
import { useMemo, useState } from "react";
import { User } from "../../types";

import { getTokenFromLocalStorage } from "../lib/local-storage";
import { SocketRPC, socketEmit } from "../lib/socket";

import { toast } from 'react-toastify';

interface Props {
	user: User,
	superAdminRoles: any, // TODO type
}

function isPasswordStrongEnough(password: string) {
	if (!/^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z])(?=.*[^0-9a-zA-Z]).{8,}$/.test(password)) {
		return false;
	}
	if (/relay/i.test(password)) {
		return false;
	}
	return true;
}

const UserCard = ({ user, superAdminRoles }: Props) => {
	const partnerString = user.partnerKey ? `PARTNER: ${user.partnerKey}` : '';
	const producerString = user.producerKey ? `PRODUCER: ${user.producerKey}` : '';
	const orderSourceString = user.orderSourceKey ? `ORDER SOURCE: ${user.orderSourceKey}` : '';

	const userOrgStrings = [partnerString, producerString, orderSourceString].filter(Boolean);

	const roleNames = user.roleKeys?.map((roleKey) => {
		const role = superAdminRoles.find((role) => role.roleKey === roleKey);
		return role?.roleName;
	}) || [];

	const [newPassword, setNewPassword] = useState<string | null>(null);
	const [isNewPasswordStrong, setIsNewPasswordStrong] = useState(true);
	const [newPasswordVerify, setNewPasswordVerify] = useState<string | null>(null);
	const [newPasswordMatches, setNewPasswordMatches] = useState(true);
	const [passwordUpdated, setPasswordUpdated] = useState(false);

	useMemo(() => {
		if (!newPassword) {
			setIsNewPasswordStrong(true);
			return setPasswordUpdated(false);
		}

		setIsNewPasswordStrong(isPasswordStrongEnough(newPassword));

		if (isNewPasswordStrong && newPassword === newPasswordVerify) {
			setNewPasswordMatches(true);
			setPasswordUpdated(true);
		}

	}, [newPassword]);

	useMemo(() => {
		if (!newPasswordVerify) {
			return setPasswordUpdated(false);
		}

		if (isNewPasswordStrong && newPassword === newPasswordVerify) {
			setNewPasswordMatches(true);
			return setPasswordUpdated(true);
		}

		setNewPasswordMatches(false);
	}, [newPasswordVerify]);

	const onSave = () => {
		const token = getTokenFromLocalStorage();

		console.log('ATTEMPTING TO SAVE');

		return socketEmit(SocketRPC.UpdateUser, {token, newPassword})
			.then((res) => {
				if (!res.success) {
					throw new Error(res.error);
				}
				return toast.success(`Successfully updated`);
			})
			.catch((err) => {
				console.error(err);
				toast.error(
					'Failed to update'
				);
			});
	}

	return (
		<Card className="bg-blue-gray-50/50 mx-8 mb-4">
			<CardBody>
				<div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr]">
					<div>
						<Typography className="font-semibold pb-2">
							Account:
						</Typography>
						<form className="w-full max-w-sm">
							<div className="mb-2 flex flex-col gap-4">
								{/* <Input type="tel" label="Phone" placeholder={user.phone} /> */}
								<Input
									type="password"
									crossOrigin=""
									label="Update Password"
									error={!isNewPasswordStrong}
									onChange={(e) => setNewPassword(e.target.value === '' ? null : e.target.value)} />
								<Input
									type="password"
									crossOrigin=""
									label="Verify Password"
									error={!newPasswordMatches}
									onChange={(e) => setNewPasswordVerify(e.target.value === '' ? null : e.target.value)
									} />
								<Typography
									variant="small"
									color="gray"
									className="flex items-center gap-1"
								>
									Use at least 8 characters, one uppercase, one lowercase, one number and one special character.
								</Typography>
							</div>
							<Button 
								className="mt-6" 
								disabled={!passwordUpdated} 
								fullWidth
								onClick={onSave}
							>
								Save
							</Button>
						</form>
					</div>

					<div className="h-0.5 my-6 w-full bg-blue-gray-100 md:mx-6 md:my-0 md:w-0.5 md:h-full"></div>

					<div>
						<Typography className="font-semibold">
							Order Configuration:
						</Typography>
						<div className="flex flex-wrap pb-8">
							{userOrgStrings.map((orgString) => {
								return (
									<Chip
										key={orgString}
										color="pink"
										// variant="ghost"
										value={orgString}
										size="sm"
										className="w-[fit-content] m-1 capitalize leading-none"
									/>
								);
							})}
						</div>
						<Typography className="font-semibold">
							Roles:
							<div className="flex flex-wrap">
								{roleNames.map((roleName) => {
									return (
										<Chip
											key={roleName}
											color="blue"
											// variant="ghost"
											value={roleName}
											size="sm"
											className="w-[fit-content] m-1"
										/>
									);
								})}
							</div>
						</Typography>
					</div>
				</div>
			</CardBody>
		</Card>
	);
};

export default UserCard;
